import React, { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AgentAvatar from "../AgentAvatar";
import { AVATAR_SIZE, TEXT_INDENT } from "./conversationConstants";
import {
  FormSurface,
  StatusPill,
  useFormPalette,
} from "../forms/DynamicFormPrimitives";
import { buildScopeChangeRows } from "./scopeChangeDiff";
import { parseMaybeJson } from "./conversationUtils";

const ACTION_SCOPE_CHANGE = "scope_change";

function truncate(value, max = 240) {
  const str = typeof value === "string" ? value : String(value ?? "");
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

// Humanize a tool-arg key (camelCase / snake_case) into a readable field label,
// e.g. "to" → "To", "runWorkflowConfigs" → "Run Workflow Configs".
function formatArgLabel(key) {
  const spaced = String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!spaced) return String(key);
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Render any tool-arg value as a short string: primitive lists join with commas,
// objects/complex lists fall back to truncated JSON, scalars are truncated text.
function formatArgValue(value) {
  if (Array.isArray(value)) {
    const allPrimitive = value.every(
      (v) => v === null || ["string", "number", "boolean"].includes(typeof v)
    );
    return allPrimitive
      ? value.map((v) => String(v)).join(", ")
      : truncate(JSON.stringify(value), 600);
  }
  if (value !== null && typeof value === "object") {
    return truncate(JSON.stringify(value), 600);
  }
  return truncate(String(value));
}

function FieldRow({ label, children }) {
  const palette = useFormPalette();
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
      <Typography
        sx={{
          fontSize: "12px",
          color: palette.textSecondary,
          minWidth: 64,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: "12px", color: palette.textPrimary, flex: 1 }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * Inline approval gate card for any tool call (or self-config scope change) that the agent paused
 * on. The guarded tool returns a PENDING envelope as its output, so the part is "output-available"
 * the instant the gate fires; the resolved (APPROVED/REJECTED) state is driven by the envelope
 * status flipping after a human decides (live resume or history reload via markToolPartCompleted).
 * Mirrors AskHumanCard's pending/resolved single-layout pattern.
 */
export default function ActionApprovalCard({
  args,
  parsedInput = null,
  result,
  agentName,
  agentId,
  grouped,
  chatSessionId,
  currentEnabledTools = [],
  currentAgentScope = null,
  onResolve = null,
  onOpenAgentSettings = null,
}) {
  const palette = useFormPalette();
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);
  const parsedArgs = useMemo(() => {
    if (parsedInput && typeof parsedInput === "object") {
      return parsedInput;
    }
    const fromArgs = parseMaybeJson(args);
    if (fromArgs && typeof fromArgs === "object") {
      return fromArgs;
    }
    const fromEnvelope = parsedResult?.requestedArgs;
    if (fromEnvelope && typeof fromEnvelope === "object") {
      return fromEnvelope;
    }
    return null;
  }, [parsedInput, args, parsedResult]);

  const status = parsedResult?.status || "PENDING";
  const isPending = status === "PENDING";
  const isApproved = status === "APPROVED";
  const isRejected = status === "REJECTED";

  const approvalId = parsedResult?.approvalId || null;
  const actionKey = parsedResult?.actionKey || null;
  const toolName = parsedResult?.toolName || "";
  const summary = parsedResult?.summary || "";
  const isScopeChange = actionKey === ACTION_SCOPE_CHANGE;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const canResolve =
    typeof onResolve === "function" && Boolean(approvalId) && !submitting;

  const avatarClick =
    agentId && typeof onOpenAgentSettings === "function"
      ? (e) => {
          e.stopPropagation();
          onOpenAgentSettings(agentId);
        }
      : undefined;

  const effectiveCurrentScope = useMemo(() => {
    if (currentAgentScope && typeof currentAgentScope === "object") {
      return {
        ...currentAgentScope,
        enabledTools:
          currentAgentScope.enabledTools ?? currentEnabledTools ?? [],
      };
    }
    return { enabledTools: currentEnabledTools || [] };
  }, [currentAgentScope, currentEnabledTools]);

  // The scope-change diff is rendered entirely client-side from the requested args and the
  // agent's current loadout — there is one diff engine (buildScopeChangeRows), so pending and
  // resolved cards always show the same complete set of changes.
  const scopeChangeRows = useMemo(() => {
    if (!isScopeChange) return [];
    return buildScopeChangeRows(parsedArgs, effectiveCurrentScope);
  }, [isScopeChange, parsedArgs, effectiveCurrentScope]);

  const showScopeSummary =
    !isScopeChange || scopeChangeRows.length === 0;

  const headerLabel = isScopeChange ? "Configuration change" : "Action approval";

  const doResolve = async (decision) => {
    if (!canResolve) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Pass this card's agentId so a team worker's approval resumes with the worker's agent
      // (the resolve handler is otherwise bound to the viewed/parent agent). Runtime config still
      // prefers the worker's persisted session server-side; this only fixes the body fallback.
      const ok = await onResolve(chatSessionId, { approvalId, decision, agentId });
      if (ok === false) {
        setSubmitError("Couldn't submit your decision. Please try again.");
        setSubmitting(false);
      }
      // On success the resumed session streams the flipped part, switching this card to its
      // resolved state, so keep controls locked.
    } catch {
      setSubmitError("Couldn't submit your decision. Please try again.");
      setSubmitting(false);
    }
  };

  const renderScopeChangeBody = () => {
    const multiSection = scopeChangeRows.length > 1;
    return (
      <Box>
        {scopeChangeRows.map((row) => (
          <Box key={row.sectionLabel} sx={{ mb: multiSection ? 1 : 0 }}>
            {row.from !== undefined && row.to !== undefined ? (
              <FieldRow label={row.sectionLabel}>
                {row.from} → {row.to}
              </FieldRow>
            ) : (
              <>
                {row.replaced && row.adding?.length > 0 && (
                  <FieldRow label={row.sectionLabel}>
                    {row.adding.join(", ")}
                  </FieldRow>
                )}
                {!row.replaced &&
                  row.adding?.map((label) => (
                    <FieldRow
                      key={`add-${row.sectionLabel}-${label}`}
                      label={multiSection ? `Adding (${row.sectionLabel})` : "Adding"}
                    >
                      {label}
                    </FieldRow>
                  ))}
                {row.removing?.map((label) => (
                  <FieldRow
                    key={`rm-${row.sectionLabel}-${label}`}
                    label={multiSection ? `Removing (${row.sectionLabel})` : "Removing"}
                  >
                    {label}
                  </FieldRow>
                ))}
                {row.setting?.length > 0 && (
                  <FieldRow label={row.sectionLabel}>
                    {row.setting.join(", ")}
                  </FieldRow>
                )}
              </>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  // Generic tool-args renderer: one labeled row per provided argument. Every gated tool
  // (including send_email) goes through this — there are no per-tool special cases.
  const renderToolArgsBody = () => {
    const entries =
      parsedArgs && typeof parsedArgs === "object"
        ? Object.entries(parsedArgs).filter(
            ([, value]) => value !== null && value !== undefined && value !== ""
          )
        : [];
    if (entries.length === 0) {
      return (
        <Typography sx={{ fontSize: "13px", color: palette.textPrimary }}>
          {summary || `Run tool: ${toolName || "unknown"}`}
        </Typography>
      );
    }
    return (
      <Box>
        {entries.map(([key, value]) => (
          <FieldRow key={key} label={formatArgLabel(key)}>
            {formatArgValue(value)}
          </FieldRow>
        ))}
      </Box>
    );
  };

  const renderBody = () => {
    if (isScopeChange) {
      if (scopeChangeRows.length > 0) {
        return renderScopeChangeBody();
      }
      return (
        <Typography sx={{ fontSize: "13px", color: palette.textPrimary }}>
          {summary || "Update agent configuration"}
        </Typography>
      );
    }

    return renderToolArgsBody();
  };

  const rightSlot = isApproved ? (
    <StatusPill label="Approved" tone="success" />
  ) : isRejected ? (
    <StatusPill label="Declined" tone="neutral" formatLabel={false} />
  ) : (
    <StatusPill label="Needs approval" tone="warning" formatLabel={false} />
  );

  return (
    <Box sx={{ px: 2.5, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar
            agentId={agentId}
            name={agentName}
            size={AVATAR_SIZE}
            onClick={avatarClick}
          />
        )}
        <Box sx={{ width: "75%", minWidth: "280px" }}>
          <FormSurface title={headerLabel} rightSlot={rightSlot}>
            {summary && showScopeSummary && (
              <Typography
                sx={{
                  fontSize: "14px",
                  color: palette.textPrimary,
                  mb: 1,
                }}
              >
                {summary}
              </Typography>
            )}

            {renderBody()}

            {isPending && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.5,
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={!canResolve}
                  onClick={() => doResolve("approve")}
                  startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: "none", fontSize: "12px" }}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canResolve}
                  onClick={() => doResolve("reject")}
                  startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: "none", fontSize: "12px" }}
                >
                  Reject
                </Button>
                {submitting && (
                  <CircularProgress size={14} sx={{ color: palette.accent }} />
                )}
              </Box>
            )}

            {submitError && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: palette.errorText,
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                {submitError}
              </Typography>
            )}
          </FormSurface>
        </Box>
      </Box>
    </Box>
  );
}
